import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AnimatePresence } from "framer-motion";

// Pages
import Onboarding from "./pages/Onboarding";
import DemoOnboarding from "./pages/DemoOnboarding";
import Login from "./pages/Login";
import Register from "./pages/Register";
import MagicLogin from "./pages/MagicLogin";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import DashboardLayout from "./layouts/DashboardLayout";
import Kreation from "./pages/app/Kreation";
import ContentPlan from "./pages/app/ContentPlan";
import Produktion from "./pages/app/Produktion";
import Distribution from "./pages/app/Distribution";
import Settings from "./pages/app/Settings";
import BrandDnaView from "./pages/app/BrandDnaView";
import Admin from "./pages/app/Admin";
import AdminPackages from "./pages/app/AdminPackages";
import PostPeerMapping from "./pages/app/PostPeerMapping";
import TrialEnd from './pages/TrialEnd';
import Upgrade from './pages/Upgrade';
import Showcase from './pages/Showcase';
import IntegrationsCallback from './pages/IntegrationsCallback';

// Auth Context
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";

// Usage Context for token limits
import { UsageProvider } from "./components/ui/UsageStatus";
import { SubscriptionProvider } from "./context/SubscriptionContext";

// Live-Chat + Analytics mit Cookiebot-Consent-Gate
import CrispChat from "./components/CrispChat";
import PostHogGate from "./components/PostHogGate";

import "./App.css";

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Laden...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Landing redirect
const LandingRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Laden...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/app/kreation" replace />;
  }
  
  return <Navigate to="/demo" replace />;
};

function AppRoutes() {
  return (
    <AnimatePresence mode="wait">
      <Routes>
        <Route path="/" element={<LandingRedirect />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/demo" element={<DemoOnboarding />} />
        <Route path="/showcase/:language" element={<Showcase />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/magic-login" element={<MagicLogin />} />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/datenschutz" element={<Datenschutz />} />
        <Route path="/integrations/callback" element={<IntegrationsCallback />} />
        
        {/* Protected App Routes */}
        <Route path="/app" element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/app/kreation" replace />} />
          <Route path="kreation" element={<Kreation />} />
          <Route path="kreation/plan" element={<ContentPlan />} />
          <Route path="produktion" element={<Produktion />} />
          <Route path="distribution" element={<Distribution />} />
          <Route path="einstellungen" element={<Settings />} />
          <Route path="brand-dna" element={<BrandDnaView />} />
          <Route path="admin" element={<Admin />} />
          <Route path="admin/packages" element={<AdminPackages />} />
          <Route path="admin/postpeer" element={<PostPeerMapping />} />
          <Route path="trial-end" element={<TrialEnd />} />
          <Route path="upgrade" element={<Upgrade />} />
        </Route>
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <UsageProvider>
            <SubscriptionProvider>
              <AppRoutes />
              <PostHogGate />
              <CrispChat />
              <Toaster
                position="top-right" 
                toastOptions={{
                  style: {
                    background: 'hsl(240 17% 8%)',
                    border: '1px solid hsl(240 10% 16%)',
                    color: 'white',
                  },
                }}
              />
            </SubscriptionProvider>
          </UsageProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;